import axios from 'axios'

const API_BASE_URL = 'https://empresa-service.intrare.app'
// Para WebSocket, usar wss (seguro) en lugar de ws, y probar con api.intrare.app
const WS_BASE_URL = 'wss://empresa-service.intrare.app'

class NotificationService {
  constructor() {
    this.ws = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectInterval = 3000
    this.reconnectTimer = null
    this.wsListeners = []
    this.pollingTimer = null
    this.pollingInterval = 5000
    this.lastNotificationIds = new Set()
    this.audioContext = null
    this.soundEnabled = true
  }

  getAuthHeaders() {
    const token = localStorage.getItem('user-token')
    return {
      'Authorization': `Token ${token}`,
      'Content-Type': 'application/json'
    }
  }

  // Obtener todas las notificaciones
  async getNotifications(filters = {}) {
    try {
      let url = `${API_BASE_URL}/notifications/`
      const params = new URLSearchParams()

      if (filters.isRead !== undefined) {
        params.append('is_read', filters.isRead)
      }

      if (params.toString()) {
        url += `?${params.toString()}`
      }

      const response = await axios.get(url, {
        headers: this.getAuthHeaders()
      })

      return response.data
    } catch (error) {
      console.error('Error obteniendo notificaciones:', error)
      throw error
    }
  }

  // Obtener contador de no leídas
  async getUnreadCount() {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/notifications/unread-count/`,
        { headers: this.getAuthHeaders() }
      )
      return response.data.count
    } catch (error) {
      console.error('Error obteniendo contador de no leídas:', error)
      throw error
    }
  }

  // Marcar una notificación como leída
  async markAsRead(notificationId) {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/notifications/${notificationId}/read/`,
        {},
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error) {
      console.error('Error marcando notificación como leída:', error)
      throw error
    }
  }

  // Marcar todas como leídas
  async markAllAsRead() {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/notifications/read-all/`,
        {},
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error) {
      console.error('Error marcando todas como leídas:', error)
      throw error
    }
  }

  // Conectar WebSocket para notificaciones en tiempo real
  connectWebSocket(onNotificationReceived) {
    const token = localStorage.getItem('user-token')
    if (!token) {
      console.warn('[WebSocket] No hay token disponible para conectar')
      return
    }

    const wsUrl = `${WS_BASE_URL}/ws/notifications/?token=${token}`

    try {
      this.ws = new WebSocket(wsUrl)

      this.ws.onopen = () => {
        this.reconnectAttempts = 0
      }

      this.ws.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data)
          const mappedNotification = this.mapWebSocketToFrontend(notification)
          onNotificationReceived(mappedNotification)
        } catch (error) {
          console.error('[WebSocket] Error procesando:', error)
        }
      }

      this.ws.onerror = (error) => {
        console.error('[WebSocket] Error en conexión')
        console.error('[WebSocket] Detalles:', error)
        if (this.ws) {
          console.error('[WebSocket] ReadyState:', this.ws.readyState)
        }
        this.startPolling(onNotificationReceived)
      }

      this.ws.onclose = (event) => {
        // Si el error es 502, usar polling como fallback
        if (event.code === 1006) {
          this.startPolling(onNotificationReceived)
        } else {
          this.attemptReconnect(onNotificationReceived)
        }
      }
    } catch (error) {
      console.error('[WebSocket] Excepción creando conexión:', error)
      this.attemptReconnect(onNotificationReceived)
    }
  }

  // Intentar reconectar
  attemptReconnect(onNotificationReceived) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      const delay = this.reconnectInterval * this.reconnectAttempts
      this.reconnectTimer = setTimeout(() => {
        this.connectWebSocket(onNotificationReceived)
      }, delay)
    } else {
      console.error('[WebSocket] Máximo de intentos de reconexión alcanzado')
    }
  }

  // Mapear datos de WebSocket al formato esperado por el frontend
  mapWebSocketToFrontend(wsNotification) {
    const typeMap = {
      'invitation': 'info',
      'access_granted': 'success',
      'access_denied': 'error',
      'waiting_room': 'warning',
      'sign_exit_pass': 'warning',
      'validate_user': 'info',
      'shipment_confirmed': 'success',
      'shipment_arrived': 'success',
      'shipment_status': 'info'
    }

    return {
      id: wsNotification.notification_id,
      title: wsNotification.title,
      message: wsNotification.body,
      type: typeMap[wsNotification.type] || 'info',
      isRead: wsNotification.is_read,
      createdAt: new Date(wsNotification.created_at),
      webUrl: wsNotification.web_url,
      entityType: wsNotification.entity_type,
      entityId: wsNotification.entity_id
    }
  }

  // Desconectar WebSocket
  disconnectWebSocket() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      this.ws.onclose = null
      this.ws.close()
      this.ws = null
    }
  }

  // Iniciar polling como fallback si WebSocket falla
  startPolling(onNotificationReceived) {

    this.pollingTimer = setInterval(async () => {
      try {
        const notifications = await this.getNotifications()
        if (Array.isArray(notifications)) {
          notifications.forEach(notif => {
            // Solo procesar notificaciones nuevas
            if (!this.lastNotificationIds.has(notif.id)) {
              this.lastNotificationIds.add(notif.id)
              onNotificationReceived(notif)
            }
          })
        }
      } catch (error) {
        console.error('[Polling] Error en polling:', error)
      }
    }, this.pollingInterval)
  }

  // Detener polling
  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
    }
  }

  // Reproducir sonido de notificación
  playNotificationSound() {
    if (!this.soundEnabled) {
      return
    }

    try {
      // Usar Web Audio API para generar sonido
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
      }

      const ctx = this.audioContext
      const now = ctx.currentTime

      // Crear oscilador para generar sonido
      const oscillator = ctx.createOscillator()
      const gainNode = ctx.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(ctx.destination)

      // Configurar sonido: dos notas para un "ping" agradable
      oscillator.frequency.setValueAtTime(800, now)
      oscillator.frequency.setValueAtTime(1200, now + 0.1)

      gainNode.gain.setValueAtTime(0.3, now)
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.2)

      oscillator.start(now)
      oscillator.stop(now + 0.2)

    } catch (error) {
      console.error('[Sound] Error reproduciendo sonido:', error)
    }
  }

  // Habilitar/deshabilitar sonido
  setSoundEnabled(enabled) {
    this.soundEnabled = enabled
  }

  // Verificar si está conectado
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN
  }
}

export default new NotificationService()